import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import Sheet from "components/sheet/Sheet";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Portal from "elements/Portal";
import CheckboxBlock from "../elements/CheckboxBlock";
import SelectNew from "components/select/SelectNew";

interface IAssignedAttributeOption {
  id: string;
  label: string;
}

interface ICustomInputEditProps {
  label: string;
  helpText?: string;
  required: boolean;
  assignedAttribute?: string;
  assignedAttributeOptions?: IAssignedAttributeOption[];
  saveInput: (label: string, value: string, helpText: string, assignedAttribute: string, required: boolean) => void;
  editModalOpen: boolean;
  closeEditModal: () => void;
  /** Viewable INPUT on the edit page itself. */
  children: React.ReactNode;
  hideRequired: boolean;
}

/** Structural display wrapper containing edit modal and its state. */
export default function CustomInputEdit(props: ICustomInputEditProps) {
  const {
    label,
    helpText = "",
    required = false,
    assignedAttribute,
    assignedAttributeOptions,
    editModalOpen,
    closeEditModal,
    hideRequired,
  } = props;
  const { t } = useTranslation();

  const [state, setState] = useState({
    label,
    helpText,
    required,
    displayLength: 25, // Not connected
    maxLength: undefined, // Not connected
    assignedAttribute,
  });

  return (
    <div className={`input-view-wrap${required ? " required" : ""}`}>
      {props.children}

      {/* EDITING  modal */}
      <Portal isMounted={editModalOpen}>
        <Sheet
          open={editModalOpen}
          title={t("Textbox")}
          onOk={() => props.saveInput(state.label, null, state.helpText, state.assignedAttribute, state.required)}
          onCancel={() => {
            closeEditModal();
            setState(prev => ({
              ...prev,
              label,
              helpText,
              assignedAttribute,
              required,
            }));
          }}
          size="small"
          okText={t("Save")}
        >
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  label={t("Question")}
                  defaultValue={label}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setState(prev => ({ ...prev, label: e.target.value }))
                  }
                  className="input-edit-action"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  label={t("Help Text")}
                  defaultValue={helpText}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setState(prev => ({ ...prev, helpText: e.target.value }))
                  }
                  className="input-edit-action"
                />
              </FormLayout.Group>
              {assignedAttributeOptions?.length > 0 && (
                <FormLayout.Group>
                  <SelectNew
                    id="custom_input_attribute"
                    value={state.assignedAttribute}
                    onChange={value => setState(prev => ({ ...prev, assignedAttribute: value }))}
                    onClear={() => setState(prev => ({ ...prev, assignedAttribute: null }))}
                    label={t("Attribute")}
                    isLoading={false}
                    searchable
                    options={assignedAttributeOptions}
                  />
                </FormLayout.Group>
              )}
              {!hideRequired ? (
                <FormLayout.Group>
                  <div />
                  <CheckboxBlock
                    label={t("Required")}
                    checked={state.required}
                    className="input-view-required"
                    onChange={() => setState(prev => ({ ...prev, required: !prev.required }))}
                  />
                </FormLayout.Group>
              ) : null}
            </FormLayout>
          </Form>
        </Sheet>
      </Portal>
    </div>
  );
}
